// =========================================================
// Vojjin Sorting Utilities
// =========================================================
import {registerGlobal} from "./core.js";

class Sorter {

    constructor(container, {
        itemSelector = null,
        handleSelector = null,
        axis = 'vertical',
        onStart = () => {
        },
        onMove = () => {
        },
        onDrop = () => {
        },
        // Optional (sibling, dragged) => boolean. When provided, only
        // siblings for which it returns true are considered valid drop
        // targets during the drag. Used to keep children inside their
        // parent's group (and top-level rows among themselves).
        siblingFilter = null,
    } = {}) {
        this.container = container;
        this.itemSelector = itemSelector || container.firstElementChild?.tagName.toLowerCase();
        this.handleSelector = handleSelector;
        this.axis = axis || 'vertical';
        this.onStart = onStart;
        this.onMove = onMove;
        this.onDrop = onDrop;
        this.siblingFilter = siblingFilter;
        this.dragged = null;
        this.placeholder = null;
        this.startY = 0;
        this.startX = 0;
        this.bindEvents();
    }

    bindEvents() {
        this.container.addEventListener('mousedown', this.handleDown.bind(this));
        this.container.addEventListener('touchstart', this.handleDown.bind(this), {passive: false});
    }

    handleDown(e) {
        if (e.target.matches('input, button, a, textarea, select')) return;
        const target = e.target.closest(this.handleSelector || this.itemSelector);
        if (!target || !this.container.contains(target)) return;
        this.dragged = target.closest(this.itemSelector);
        if (!this.dragged) return;
        e.preventDefault();

        const rect = this.dragged.getBoundingClientRect();

        // Create placeholder
        this.placeholder = document.createElement('div');
        this.placeholder.classList.add('sortable-placeholder');
        this.placeholder.style.height = `${rect.height}px`;
        this.placeholder.style.width = `${rect.width}px`;

        // Insert placeholder where dragged element was
        this.container.insertBefore(this.placeholder, this.dragged.nextSibling);

        // Set absolute positioning for dragged element
        this.dragged.style.position = 'absolute';
        this.dragged.style.top = `${rect.top + window.scrollY}px`;
        this.dragged.style.left = `${rect.left + window.scrollX}px`;
        this.dragged.style.width = `${rect.width}px`;
        this.dragged.style.zIndex = '1000';
        this.dragged.classList.add('dragging');

        this.startY = e.touches ? e.touches[0].clientY : e.clientY;
        this.startX = e.touches ? e.touches[0].clientX : e.clientX;

        // Append to body (so it floats above layout)
        document.body.appendChild(this.dragged);

        this.onStart(this.dragged);

        document.addEventListener('mousemove', this.handleMoveBound = this.handleMove.bind(this));
        document.addEventListener('touchmove', this.handleMoveBound, {passive: false});
        document.addEventListener('mouseup', this.handleUpBound = this.handleUp.bind(this));
        document.addEventListener('touchend', this.handleUpBound);
    }

    handleMove(e) {
        e.preventDefault();
        if (!this.dragged) return;

        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const dy = clientY - this.startY;
        const dx = clientX - this.startX;

        if (this.axis === 'vertical') {
            this.dragged.style.transform = `translateY(${dy}px)`;
        } else if (this.axis === 'horizontal') {
            this.dragged.style.transform = `translateX(${dx}px)`;
        } else {
            this.dragged.style.transform = `translate(${dx}px, ${dy}px)`;
        }

        let siblings = Array.from(this.container.querySelectorAll(this.itemSelector))
            .filter(el => el !== this.dragged && el !== this.placeholder);

        if (typeof this.siblingFilter === 'function') {
            siblings = siblings.filter(el => this.siblingFilter(el, this.dragged));
        }

        for (const sib of siblings) {
            const sibRect = sib.getBoundingClientRect();
            if (clientY < sibRect.top + sibRect.height / 2) {
                this.container.insertBefore(this.placeholder, sib);
                break;
            } else if (clientY > sibRect.bottom - sibRect.height / 2) {
                this.container.insertBefore(this.placeholder, sib.nextSibling);
            }
        }

        this.onMove(this.dragged, this.placeholder);
    }

    handleUp() {
        if (!this.dragged) return;

        // Reset absolute styles
        this.dragged.style.position = '';
        this.dragged.style.top = '';
        this.dragged.style.left = '';
        this.dragged.style.width = '';
        this.dragged.style.zIndex = '';
        this.dragged.style.transform = '';

        this.dragged.classList.remove('dragging');
        this.container.insertBefore(this.dragged, this.placeholder);
        this.placeholder.remove();

        // Cleanup listeners
        document.removeEventListener('mousemove', this.handleMoveBound);
        document.removeEventListener('touchmove', this.handleMoveBound);
        document.removeEventListener('mouseup', this.handleUpBound);
        document.removeEventListener('touchend', this.handleUpBound);

        this.onDrop(this.dragged);
        this.dragged = null;
        this.placeholder = null;
    }
}

registerGlobal('Sorter', Sorter);